import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { CollapseSection, getFormattedDateTime, Modal } from "@msys/ui";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AdjustIcon from "@mui/icons-material/Adjust";
import CheckIcon from "@mui/icons-material/Check";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { useTolgee, useTranslate } from "@tolgee/react";
import { Link } from "react-router-dom";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { QuoteTemplateDiscardUnpublishedChangesButton } from "./buttons/QuoteTemplateDiscardUnpublishedChangesButton.js";
import { QuoteTemplatePublishButton } from "./buttons/QuoteTemplatePublishButton.js";
import { QuoteTemplateRestoreTemplateVersionButton } from "./buttons/QuoteTemplateRestoreTemplateVersionButton.js";
import { QuoteTemplateShareButton } from "./buttons/QuoteTemplateShareButton.js";
import { QuoteTemplateUnshareFromStoreButton } from "./buttons/QuoteTemplateUnshareFromStoreButton.js";
import { QuoteTemplateUpdateShareButton } from "./buttons/QuoteTemplateUpdateShareButton.js";
import { useTemplatesQuoteTemplateManageVersionsModalQuery } from "./TemplatesQuoteTemplateManageVersionsModal.generated.js";

interface Props {
  docId: string;
  pathToDoc: string;
  handleClose: () => void;
  refetchQueries?: string[];
}

export const TemplatesQuoteTemplateManageVersionsModal = ({
  pathToDoc,
  docId,
  handleClose,
  refetchQueries,
}: Props) => {
  const { t } = useTranslate(["TemplatesSharing", "Global"]);
  const language = useTolgee(["language"]).getLanguage()!;

  const client = useApolloClient();
  const query = useTemplatesQuoteTemplateManageVersionsModalQuery({
    client,
    variables: { id: docId },
  });

  const doc = getDataOrNull(query.data?.quoteTemplateLatest)?.quoteTemplate;
  if (!doc) return null;

  const sharedVersionNumber =
    doc.templateSharing?.latestSharedVersionNumber ?? null;
  const sharedPublicly = !!(
    doc.templateSharing && doc.templateSharing.sharedPublicly
  );
  const lastPublishedVersion = doc.publishedVersions[0];
  const hasAnyPublishedVersion = doc.hasAnyPublishedVersion;

  const isSharedVersionNumber = (versionNumber: number) =>
    (versionNumber && versionNumber === sharedVersionNumber) ||
    (versionNumber === lastPublishedVersion.versionNumber &&
      hasAnyPublishedVersion);

  const canBeSharedToMeisterStore = (versionNumber: number) =>
    versionNumber === lastPublishedVersion.versionNumber &&
    hasAnyPublishedVersion &&
    !sharedVersionNumber;

  const canBeSharedPubliclyToMeisterStore = (versionNumber: number) =>
    hasAnyPublishedVersion &&
    !sharedPublicly &&
    versionNumber == sharedVersionNumber;

  const canUpdateSharedVersionToMeisterStore = (versionNumber: number) =>
    hasAnyPublishedVersion &&
    sharedVersionNumber &&
    versionNumber > sharedVersionNumber;

  const canRestoreVersionNumber = (versionNumber: number) =>
    hasAnyPublishedVersion &&
    versionNumber < lastPublishedVersion.versionNumber;

  const canUnshareFromMeisterStore = (versionNumber: number) =>
    versionNumber === sharedVersionNumber;

  return (
    <Modal
      handleClose={handleClose}
      maxWidth="md"
      title={t("Manage versions", { ns: "TemplatesSharing" })}
      actionButtons={[
        {
          label: t("Close", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
      ]}
    >
      <Stack direction="column" spacing={2}>
        <CollapseSection
          title={t("Changes pending", { ns: "TemplatesSharing" })}
          isInitiallyExpanded={true}
          titleVariant="h3"
        >
          {doc.containsUnpublishedChanged ? (
            <Card variant="outlined">
              <Stack
                direction="column"
                spacing={2}
                sx={{
                  p: 1,
                }}
              >
                <Stack direction="row" spacing={2}>
                  <Avatar sx={{ bgcolor: "warning.main" }}>
                    <AccessTimeIcon />
                  </Avatar>
                  <Stack direction="column">
                    <Typography variant="h4">
                      {t("Version {number}", {
                        ns: "TemplatesSharing",
                        number: lastPublishedVersion?.versionNumber
                          ? lastPublishedVersion.versionNumber.toString()
                          : "1",
                      })}
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ ml: 1 }}
                        label={t("Unpublished changes", {
                          ns: "TemplatesSharing",
                        })}
                      />
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "grey.600",
                      }}
                    >
                      {lastPublishedVersion
                        ? getFormattedDateTime(
                            lastPublishedVersion.createdAt,
                            language
                          )
                        : "–"}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  <Button
                    component={Link}
                    to={`${pathToDoc}/latest`}
                    variant="text"
                    startIcon={<VisibilityIcon />}
                    size="small"
                    onClick={handleClose}
                  >
                    {t("View", { ns: "Global" })}
                  </Button>
                  <QuoteTemplatePublishButton
                    docId={docId}
                    pathToDoc={pathToDoc}
                    buttonProps={{ size: "small" }}
                    buttonTitle={t("Publish changes", {
                      ns: "TemplatesSharing",
                    })}
                  />
                  {hasAnyPublishedVersion && (
                    <QuoteTemplateDiscardUnpublishedChangesButton
                      docId={docId}
                      buttonProps={{ size: "small", color: "error" }}
                      buttonTitle={t("Revert changes", {
                        ns: "TemplatesSharing",
                      })}
                    />
                  )}
                </Stack>
              </Stack>
            </Card>
          ) : (
            <Alert severity="info" variant="outlined">
              {t(
                "No changes pending. Latest published version is the current version.",
                { ns: "TemplatesSharing" }
              )}
            </Alert>
          )}
        </CollapseSection>
        <CollapseSection
          title={t("Version history", { ns: "TemplatesSharing" })}
          isInitiallyExpanded={true}
          titleVariant="h3"
        >
          {doc.publishedVersions.length > 0 ? (
            <Stack direction="column" spacing={1}>
              {doc.publishedVersions.map(pv => (
                <Card variant="outlined" key={pv.id}>
                  <Stack
                    direction="column"
                    spacing={2}
                    sx={{
                      p: 1,
                    }}
                  >
                    <Stack direction="row" spacing={2}>
                      <Avatar
                        sx={{
                          bgcolor: isSharedVersionNumber(pv.versionNumber)
                            ? "success.main"
                            : undefined,
                        }}
                      >
                        {isSharedVersionNumber(pv.versionNumber) ? (
                          <CheckIcon />
                        ) : (
                          <AdjustIcon />
                        )}
                      </Avatar>
                      <Stack direction="column">
                        <Typography variant="h4">
                          {t("Version {number}", {
                            ns: "TemplatesSharing",
                            number: pv.versionNumber.toString(),
                          })}
                          {pv.versionNumber ===
                            lastPublishedVersion.versionNumber && (
                            <Chip
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ ml: 1 }}
                              label={t("Latest published", {
                                ns: "TemplatesSharing",
                              })}
                            />
                          )}
                          {sharedPublicly &&
                            pv.versionNumber === sharedVersionNumber && (
                              <Chip
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ ml: 1 }}
                                label={t("Meister Store", {
                                  ns: "TemplatesSharing",
                                })}
                              />
                            )}
                          {!sharedPublicly &&
                            pv.versionNumber === sharedVersionNumber && (
                              <Chip
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ ml: 1 }}
                                label={`${t("Meister Store", {
                                  ns: "TemplatesSharing",
                                })} (${t("{count, plural, =0 {No companies} one {One company} other {# companies}}", { ns: "TemplatesSharing", count: doc.templateSharing ? doc.templateSharing.sharedWithOrganisations.length : 0 })})`}
                              />
                            )}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "grey.600",
                          }}
                        >
                          {getFormattedDateTime(pv.createdAt, language)}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: "center",
                      }}
                    >
                      <Button
                        component={Link}
                        to={`${pathToDoc}/${pv.versionNumber}`}
                        variant="text"
                        startIcon={<VisibilityIcon />}
                        size="small"
                        onClick={handleClose}
                      >
                        {t("View", { ns: "Global" })}
                      </Button>
                      {canRestoreVersionNumber(pv.versionNumber) && (
                        <QuoteTemplateRestoreTemplateVersionButton
                          docId={docId}
                          buttonProps={{ size: "small" }}
                          buttonTitle={t("Restore version", {
                            ns: "TemplatesSharing",
                          })}
                          restoreToSpecificVersionNumber={pv.versionNumber}
                        />
                      )}
                      {canBeSharedToMeisterStore(pv.versionNumber) && (
                        <RestrictedByCapabilityWithDebug capability="TEMPLATE_SHARING">
                          <RestrictedByOrganisationPermissionWithDebug permission="SHARE_TEMPLATE">
                            <>
                              <QuoteTemplateShareButton
                                doc={doc}
                                buttonProps={{ size: "small" }}
                                buttonTitle={t("Share on Meister Store", {
                                  ns: "TemplatesSharing",
                                })}
                                versionNumber={pv.versionNumber}
                                refetchQueries={[
                                  namedOperations.Query
                                    .TemplatesQuoteTemplateManageVersionsModal,
                                ]}
                              />
                              <Typography variant="body2" color="textSecondary">
                                {t(
                                  "This document is not yet shared on Meister Store",
                                  { ns: "TemplatesSharing" }
                                )}
                              </Typography>
                            </>
                          </RestrictedByOrganisationPermissionWithDebug>
                        </RestrictedByCapabilityWithDebug>
                      )}
                      {canBeSharedPubliclyToMeisterStore(pv.versionNumber) && (
                        <RestrictedByCapabilityWithDebug capability="TEMPLATE_SHARING">
                          <RestrictedByOrganisationPermissionWithDebug permission="SHARE_TEMPLATE">
                            <QuoteTemplateShareButton
                              doc={doc}
                              buttonProps={{ size: "small" }}
                              buttonTitle={t(
                                "Update sharing on Meister Store",
                                {
                                  ns: "TemplatesSharing",
                                }
                              )}
                              versionNumber={pv.versionNumber}
                              refetchQueries={[
                                namedOperations.Query
                                  .TemplatesQuoteTemplateManageVersionsModal,
                              ]}
                            />
                          </RestrictedByOrganisationPermissionWithDebug>
                        </RestrictedByCapabilityWithDebug>
                      )}
                      {canUpdateSharedVersionToMeisterStore(
                        pv.versionNumber
                      ) && (
                        <RestrictedByCapabilityWithDebug capability="TEMPLATE_SHARING">
                          <RestrictedByOrganisationPermissionWithDebug permission="SHARE_TEMPLATE">
                            <QuoteTemplateUpdateShareButton
                              doc={doc}
                              buttonProps={{ size: "small" }}
                              buttonTitle={t(
                                "Update Meister Store to this version",
                                { ns: "TemplatesSharing" }
                              )}
                              versionNumber={pv.versionNumber}
                              refetchQueries={[
                                namedOperations.Query
                                  .TemplatesQuoteTemplateManageVersionsModal,
                              ]}
                            />
                          </RestrictedByOrganisationPermissionWithDebug>
                        </RestrictedByCapabilityWithDebug>
                      )}
                      {canUnshareFromMeisterStore(pv.versionNumber) && (
                        <RestrictedByCapabilityWithDebug capability="TEMPLATE_SHARING">
                          <RestrictedByOrganisationPermissionWithDebug permission="SHARE_TEMPLATE">
                            <QuoteTemplateUnshareFromStoreButton
                              doc={doc}
                              buttonProps={{ size: "small" }}
                              refetchQueries={[
                                namedOperations.Query
                                  .TemplatesQuoteTemplateManageVersionsModal,
                              ]}
                            />
                          </RestrictedByOrganisationPermissionWithDebug>
                        </RestrictedByCapabilityWithDebug>
                      )}
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <Alert severity="info" variant="outlined">
              {t("No published version of this template yet.", {
                ns: "TemplatesSharing",
              })}
            </Alert>
          )}
        </CollapseSection>
      </Stack>
    </Modal>
  );
};
